/* eslint-disable react-hooks/exhaustive-deps */
/* ============================================================
   ใบขอเบิกเพื่อเตรียมการผลิตประจำวัน

   1 Production Order → 1 ฟอร์มต่อสินค้า 1 รายการ
   ตารางหนึ่งชุดแสดงได้ 5 batch ถ้ามีมากกว่านั้นจะขึ้นตารางใหม่ต่อกันไป
   (เหมือนฟอร์มกระดาษเดิมที่มี 2 ตารางต่อหน้า)

   ช่องชุดที่ 1-5 ระบบเติมน้ำหนักที่ใช้จริงของแต่ละ batch ให้
   ช่อง Lot No. เว้นว่างไว้ให้เขียนมือหน้างาน
============================================================ */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Modal, Button } from "antd";
import dayjs from "dayjs";
import { PiPrinterFill } from "react-icons/pi";

import logo from "../../../assets/images/logo_nsf.png";
import ProductionOrderService from "../../../service/ERP/ProductionOrder.service";
import PdDocMeta from "../pd-common/PdDocMeta";
import "./pd-requisition.css";

const pdService = ProductionOrderService();

const PAGE_STYLE_ID = "pd-req-print-page";

/* จำนวนคอลัมน์ batch ต่อ 1 ตาราง — เกินกว่านี้ขึ้นตารางใหม่ */
const SETS_PER_TABLE = 5;
/* จำนวนแถวขั้นต่ำ เติมแถวว่างให้ฟอร์มเต็มเหมือนกระดาษเดิม */
const MIN_ROWS = 6;
/* จำนวนตารางต่อ 1 หน้า A4 (ฟอร์มกระดาษเดิมมี 2 ตารางต่อหน้า)
   ถ้าวัตถุดิบเยอะเกิน 2 ตารางจะล้นหน้า จึงลดเหลือ 1 ตารางต่อหน้า */
const TABLES_PER_PAGE = 2;
const MAX_ROWS_FOR_TWO_TABLES = 12;

const wnum = (v, digits = 3) =>
  Number(v || 0) > 0
    ? Number(v).toLocaleString("en-US", {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      })
    : "";

/* ไม่ต้องพิมพ์น้ำ (water) — เบิกหน้างานเอง ไม่ต้องขึ้นใบขอเบิก
   เทียบแบบไม่สนตัวพิมพ์เล็กใหญ่ และเทียบทั้งชื่อไทย/อังกฤษ */
const HIDE_MATERIALS = ["water", "น้ำ", "นํ้า"];
const isHiddenMaterial = (m = {}) => {
  const name = String(m.mat_name || "").trim().toLowerCase();
  const code = String(m.mat_code || "").trim().toLowerCase();
  return HIDE_MATERIALS.includes(name) || HIDE_MATERIALS.includes(code);
};

/* จัดกลุ่ม batch ตามวันที่ผลิต — คนละวันต้องแยกคนละหน้า A4
   batch ที่ยังไม่ระบุวันที่ ถือเป็นกลุ่มเดียวกันทั้งหมด */
const groupByDate = (batches = []) => {
  const groups = [];
  const index = new Map();
  batches.forEach((b, i) => {
    const key = b?.production_date || "";
    if (!index.has(key)) {
      index.set(key, groups.length);
      groups.push({ date: b?.production_date || null, items: [] });
    }
    /* เก็บตำแหน่งเดิมไว้ด้วย เพื่อหยิบน้ำหนักจาก per_batch ให้ตรง batch */
    groups[index.get(key)].items.push({ ...b, _idx: i });
  });
  return groups.length > 0 ? groups : [{ date: null, items: [] }];
};

const chunk = (arr, size) => {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out.length > 0 ? out : [[]];
};

/* ตารางหนึ่งชุด = batch สูงสุด 5 ชุด */
function SetTable({ materials, batches, totalSets, sumOf }) {
  const cols = Array.from({ length: SETS_PER_TABLE }, (_, i) => batches[i]);
  const blanks = Math.max(0, MIN_ROWS - materials.length);

  return (
    <table className="pdr-tbl">
      {/* รวมกันต้องได้ 100% พอดี ไม่งั้นคอลัมน์ตัวเลขจะถูกบีบจนตกบรรทัด */}
      <colgroup>
        <col style={{ width: "5%" }} />
        <col style={{ width: "11%" }} />
        <col style={{ width: "17%" }} />
        <col style={{ width: "9%" }} />
        {cols.map((_, i) => (
          <col key={i} style={{ width: "8%" }} />
        ))}
        <col style={{ width: "7%" }} />
        <col style={{ width: "4%" }} />
        <col style={{ width: "7%" }} />
      </colgroup>
      <thead>
        <tr>
          <th rowSpan={3}>ลำดับ</th>
          <th rowSpan={3}>
            รหัส
            <br />
            วัตถุดิบ
            <br />
            /ส่วนผสม
          </th>
          <th rowSpan={3}>สารส่วนผสม</th>
          <th colSpan={1 + SETS_PER_TABLE} style={{ textAlign: "left" }}>
            จำนวน <span className="pdr-set-count">{totalSets}</span> ชุด
          </th>
          <th colSpan={2} rowSpan={2}>
            รวม
          </th>
          <th rowSpan={3}>Lot. No</th>
        </tr>
        <tr>
          <th rowSpan={2}>น้ำหนัก/ชุด (kg.)</th>
          <th colSpan={SETS_PER_TABLE}>ชุดที่</th>
        </tr>
        <tr>
          {cols.map((b, i) => (
            <th key={i}>{b ? b.batch_no : ""}</th>
          ))}
          <th>kg.</th>
          <th>ชิ้น</th>
        </tr>
      </thead>
      <tbody>
        {materials.map((m, idx) => (
          <tr key={`${m.mat_code}-${idx}`}>
            <td className="c">{idx + 1}</td>
            <td className="c">{m.mat_code}</td>
            <td className="l">{m.mat_name}</td>
            <td className="r">{wnum(m.per_set)}</td>
            {/* ช่องชุดที่ 1-5 เว้นว่างให้เขียนน้ำหนักจริงหน้างาน */}
            {cols.map((_, i) => (
              <td key={i} />
            ))}
            <td className="r">{wnum(sumOf(m))}</td>
            <td />
            <td />
          </tr>
        ))}
        {Array.from({ length: blanks }).map((_, i) => (
          <tr key={`blank-${i}`}>
            <td className="c">&nbsp;</td>
            <td />
            <td />
            <td />
            {cols.map((_, j) => (
              <td key={j} />
            ))}
            <td />
            <td />
            <td />
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function PdRequisitionPrintPreview() {
  const { code } = useParams();

  const [forms, setForms] = useState([]);

  const handlePrint = () => window.print();

  /* @page scope ตาม element ไม่ได้ จึงฉีดตอนเข้าหน้าและถอดตอนออก
     (อยู่ท้าย cascade จึงชนะ @page ของหน้า print อื่นในบันเดิลเดียวกัน) */
  useEffect(() => {
    let style = document.getElementById(PAGE_STYLE_ID);
    if (!style) {
      style = document.createElement("style");
      style.id = PAGE_STYLE_ID;
      style.media = "print";
      style.innerHTML = "@page { size: A4 portrait; margin: 8mm; }";
      document.head.appendChild(style);
    }
    return () => {
      const s = document.getElementById(PAGE_STYLE_ID);
      if (s) s.remove();
    };
  }, []);

  useEffect(() => {
    pdService
      .requisition(code)
      .then((res) => {
        setForms(res.data?.data?.forms || []);
      })
      .catch((err) => {
        Modal.error({
          title: "Error",
          content: err.response?.data?.message || "โหลดข้อมูลไม่สำเร็จ",
          onOk: () => window.close(),
        });
      });
  }, [code]);

  return (
    <div className="page-show" id="pd-req">
      <div className="doc-wrapper">
        <div className="print-bar no-print">
          <Button
            className="bn-center bg-blue-400"
            onClick={handlePrint}
            icon={<PiPrinterFill style={{ fontSize: "1.1rem" }} />}
          >
            PRINT
          </Button>
        </div>

        {(() => {
          /* เตรียมทุกหน้าก่อน เพื่อใส่เลขหน้า "หน้า x / n" ได้ */
          const pages = [];
          forms.forEach((f, fi) => {
            const materials = (f.materials || []).filter((m) => !isHiddenMaterial(m));
            const perPage = materials.length > MAX_ROWS_FOR_TWO_TABLES ? 1 : TABLES_PER_PAGE;
            groupByDate(f.batches || []).forEach((g, gi) => {
              /* รวมของหน้านี้ = เฉพาะ batch ที่ผลิตวันเดียวกัน */
              const sumOf = (m) =>
                g.items.reduce((t, b) => t + Number(m.per_batch?.[b._idx] || 0), 0);
              const tables = chunk(g.items, SETS_PER_TABLE);
              /* หน้าแบบ 2 ตาราง ถ้าไม่ครบให้เติมตารางเปล่าเหมือนกระดาษเดิม */
              while (tables.length % perPage !== 0) tables.push([]);
              chunk(tables, perPage).forEach((pageTables, pi) =>
                pages.push({ key: `${f.pg_code}-${fi}-${gi}-${pi}`, f, g, materials, sumOf, pageTables })
              );
            });
          });

          return pages.map(({ key, f, g, materials, sumOf, pageTables }, idx) => (
            <section className="print-page" key={key}>
              <div className="pdr-head">
                <img src={logo} alt="" className="pdr-logo" />
                <div className="pdr-title">
                  <p className="pdr-company">บริษัทไนน์สตาร์ฟู้ด จำกัด</p>
                  <p className="pdr-doc">ใบขอเบิกส่วนผสม</p>
                </div>
              </div>

              <PdDocMeta
                form={f}
                date={g.date ? dayjs(g.date).format("DD/MM/YYYY") : dayjs().format("DD/MM/YYYY")}
              />

              <div className="pdr-body">
                {pageTables.map((t, ti) => (
                  <SetTable
                    key={ti}
                    materials={materials}
                    batches={t}
                    totalSets={g.items.length}
                    sumOf={sumOf}
                  />
                ))}
              </div>

              <div className="pdr-foot">
                <div className="pdr-sign">
                  <span>ผู้จ่าย_______________</span>
                  <span>ผู้ตรวจสอบ_______________</span>
                </div>
                <div className="pdr-pageno">
                  <span>{code}</span>
                  <span>หน้า {idx + 1} / {pages.length}</span>
                </div>
              </div>
            </section>
          ));
        })()}
      </div>
    </div>
  );
}
